import mixpanel from 'mixpanel-browser';
import type { EventName, EventPropsMap } from '@obs-schema';
import { validateAndFilter, SCHEMA_VERSION } from '@obs-schema';
import { hasAnalyticsConsent } from '../lib/consent';

const V = (import.meta as any).env || {};
const TOKEN: string | undefined = V?.VITE_MIXPANEL_TOKEN;
const API_HOST: string = V?.VITE_MIXPANEL_API_HOST || 'https://api-js.mixpanel.com';
const ENABLED: boolean = String(V?.VITE_ENABLE_ANALYTICS) === 'true';
const APP_VERSION: string | undefined = V?.VITE_APP_VERSION;
const ENV: string | undefined = V?.VITE_ENV || (V?.PROD ? 'production' : 'development');
const PROXY: boolean = String(V?.VITE_MIXPANEL_PROXY || '0') === '1';

function getOrCreateId(key: string): string {
  try {
    const v = localStorage.getItem(key);
    if (v) return v;
    const id = crypto.randomUUID ? crypto.randomUUID() : String(Math.random()).slice(2) + Date.now();
    localStorage.setItem(key, id);
    return id;
  } catch { return String(Date.now()); }
}

let initialized = false;

function hasConsent(): boolean {
  return hasAnalyticsConsent();
}

export function initMixpanel(): void {
  if (initialized) return;
  initialized = true;
  if (!ENABLED || !TOKEN || !hasConsent()) {
    try { mixpanel.init('disabled'); } catch {}
    try { mixpanel.disable(); } catch {}
    return;
  }
  try {
    mixpanel.init(TOKEN, {
      api_host: API_HOST,
      persistence: 'localStorage',
      ignore_dnt: false,
      batch_requests: true,
    } as any);
    const anon = getOrCreateId('lumi_anonymous_id');
    try { document.cookie = `lumi_anonymous_id=${encodeURIComponent(anon)}; path=/; max-age=31536000; samesite=lax`; } catch {}
    try { (mixpanel as any).identify?.(anon); } catch {}
    const sessionId = getOrCreateId('lumi_session_id');
    const superProps: Record<string, any> = { source: 'web' };
    if (APP_VERSION) superProps.app_version = APP_VERSION;
    if (ENV) superProps.env = ENV;
    superProps.session_id = sessionId;
    // If we can read the authenticated user id from the DOM/window, attach it as a super prop for parity with server
    try {
      const el = document.querySelector('meta[name="lumiplot-user-id"]') as HTMLMetaElement | null;
      const uid = el?.getAttribute('content') || (window as any).__lumiUserId || undefined;
      if (uid) superProps.$user_id = uid;
    } catch {}
    (mixpanel as any).register?.(superProps);
  } catch {}
}

export function identify(userId?: string, traits?: Record<string, unknown>): void {
  if (!ENABLED || !TOKEN || !hasConsent()) return;
  try {
    if (userId) mixpanel.identify(userId);
    if (traits) (mixpanel as any).people?.set?.(traits as any);
  } catch {}
}

export function alias(newId: string): void {
  if (!ENABLED || !TOKEN || !hasConsent()) return;
  try { if (newId) mixpanel.alias(newId); } catch {}
}

export function track<E extends EventName>(name: E, props: EventPropsMap[E]): void {
  if (!ENABLED || !TOKEN || !hasConsent()) return;
  try {
    const versioned = { ...(props as any), event_schema_version: SCHEMA_VERSION } as Record<string, any>;
    const clean = validateAndFilter(name, versioned);
    // Lightweight redaction: drop large text
    const redacted: Record<string, unknown> = {};
    for (const k of Object.keys(clean)) {
      const v = (clean as any)[k];
      if (typeof v === 'string' && v.length > 1000) { redacted[`${k}_len`] = v.length; continue; }
      redacted[k] = v;
    }
    if (PROXY) {
      try {
        const metaEl = document.querySelector('meta[name="lumiplot-user-id"]') as HTMLMetaElement | null;
        const uid = metaEl?.getAttribute('content') || (window as any).__lumiUserId || undefined;
        const distinct = uid || getOrCreateId('lumi_anonymous_id');
        const withSession = { ...redacted, session_id: getOrCreateId('lumi_session_id') };
        fetch('/api/obs/track', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ event: name, props: withSession, distinct_id: distinct }),
        }).catch(() => {});
      } catch {}
      return;
    }
    mixpanel.track(name, redacted);
  } catch {}
}

export function isAnalyticsEnabled(): boolean {
  return ENABLED && !!TOKEN && hasConsent();
}


